import type { PostHog } from 'posthog-js';

import type { DomainEvent } from '../core/domainEvents';
import { pageviewsAreHeld, subscribePageviewGate } from '../core/pageviewGate';
import type {
    AnalyticsAdapter,
    AnalyticsContextSnapshot,
    IdentifyUserPayload,
} from '../core/types';

import { resetIdentifiedFlags, trackIdentifiedFlags } from './identifiedFlags';
import { mapDomainEventToPostHog } from './mappers';

/** The PostHog calls the adapter makes, so a test can hand it a client without a cast. */
export type PostHogAdapterClient = Pick<
    PostHog,
    'capture' | 'identify' | 'group' | 'reset' | 'onFeatureFlags' | 'register' | 'unregister'
> & {
    /** Absent on test doubles, which capture immediately. `false` means the client drops events. */
    __loaded?: boolean;
};

interface QueuedCapture {
    eventName: string;
    properties: Record<string, unknown>;
    beacon: boolean;
}

const INIT_FLUSH_ATTEMPTS = 40;
/** Far enough apart that forty tries cover the time PostHog usually takes to load. */
const FLUSH_RETRY_MS = 250;

export function createPostHogAdapter(posthog: PostHogAdapterClient): AnalyticsAdapter {
    const queued: QueuedCapture[] = [];
    let flushTimer = 0;
    let flushAttempts = 0;
    let unsubscribeGate: (() => void) | null = null;

    const clientReady = (): boolean => posthog.__loaded !== false;
    const deliveryBlocked = (): boolean => !clientReady() || pageviewsAreHeld();

    const send = (item: QueuedCapture): void => {
        if (item.beacon) {
            posthog.capture(item.eventName, item.properties, { transport: 'sendBeacon' });
            return;
        }

        posthog.capture(item.eventName, item.properties);
    };

    const flushQueue = (): void => {
        if (flushTimer !== 0) {
            window.clearTimeout(flushTimer);
            flushTimer = 0;
        }

        // Identity still owns the queue. The gate subscription wakes this when it opens,
        // so a loading client must not spend its retries while the gate is shut.
        if (pageviewsAreHeld()) {
            return;
        }

        // A check that ran before the client finished loading has to schedule the next
        // one. Becoming ready does not call back into the adapter, and nothing else
        // will capture if the visitor stays idle.
        if (!clientReady()) {
            scheduleFlush();
            return;
        }

        flushAttempts = 0;
        const batch = queued.splice(0);
        for (const item of batch) {
            send(item);
        }
    };

    const scheduleFlush = (): void => {
        if (flushTimer !== 0 || flushAttempts >= INIT_FLUSH_ATTEMPTS) {
            return;
        }

        flushAttempts += 1;
        flushTimer = window.setTimeout(flushQueue, FLUSH_RETRY_MS);
    };

    const watchGate = (): void => {
        if (unsubscribeGate) {
            return;
        }

        unsubscribeGate = subscribePageviewGate(() => {
            unsubscribeGate?.();
            unsubscribeGate = null;
            if (clientReady()) {
                flushQueue();
                return;
            }

            scheduleFlush();
        });
    };

    const enqueueOrSend = (item: QueuedCapture): void => {
        if (deliveryBlocked()) {
            queued.push(item);
            if (!clientReady()) {
                scheduleFlush();
            }
            if (pageviewsAreHeld()) {
                watchGate();
            }
            return;
        }

        flushQueue();
        send(item);
    };

    return {
        capture(event: DomainEvent, context: AnalyticsContextSnapshot): void {
            const { eventName, properties } = mapDomainEventToPostHog(event, context);
            // Beacon survives the full-page redirect that follows a password sign-in.
            const beacon = event.type === 'pageLeft' || event.type === 'loggedIn';
            enqueueOrSend({ eventName, properties, beacon });
        },

        identifyUser(payload: IdentifyUserPayload): void {
            posthog.identify(payload.userId, {
                email: payload.email,
                name: payload.name,
                environment: payload.environment,
                isOptoScaleAdmin: payload.isOptoScaleAdmin,
                is_optoscale_admin: payload.isOptoScaleAdminGroup,
                isClientAdmin: payload.isClientAdmin,
                isLocationAdmin: payload.isLocationAdmin,
                clientId: payload.clientId,
                clientName: payload.clientName,
                client: payload.client,
                ...(payload.licenses ? { licenses: payload.licenses } : {}),
            });
            // No flags load can finish before the group calls below, which run in this same tick.
            trackIdentifiedFlags(posthog, payload.userId);

            if (payload.isOptoScaleAdminGroup) {
                posthog.group('company', 'optoscale', {
                    name: 'OptoScale',
                });
                return;
            }

            const client = payload.primaryClient;
            if (client) {
                posthog.group('company', String(client.id), {
                    name: client.name,
                    licenses: client.licenses,
                    locationCount: client.locationCount,
                    locationNames: client.locationNames,
                });
            }
        },

        reset(): void {
            posthog.reset();
            resetIdentifiedFlags();
        },

        setSessionFlags(flags: Record<string, unknown>): void {
            posthog.register(flags);
        },

        clearSessionFlags(keys: string[]): void {
            for (const key of keys) {
                posthog.unregister(key);
            }
        },
    };
}
